import React, { useEffect, useState, useCallback } from 'react';
import { Card, Box, Typography, Collapse, IconButton } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  getCells,
  getMerchantsInCell,
  setSelectedOrderId,
  setOrderWithinTimeFilter,
} from '../../actions/orderActions';
import { selectCells, selectMerchants } from 'selectors/ordersSelector';
import { getAllLandingZonesForCell } from '../../utils/getAllLandingZonesForCell';
import { WeigherAppOrderCard } from 'views/Operations/components/WeigherAppOrderCard';
import { LandingZone, Order, CollectorStatus, OrderBagListRequest, BagLocation } from '@m4a3/api-client';
import { AppDispatch, RootState } from '../../store/configureStore';
import OpsHeader from './components/OpsHeader';
import { orderIsInLandingZone } from '../../services/order-service';
import WeigherAppOrderCardDetails from './components/WeigherAppOrderCardDetails'; 
import { API } from 'aws-amplify';
import { logToDatadog } from '../../services/datadog';
import OrderWeightDialog from './components/OrderWeightModal';
import OrderStorageLocationModal from './components/OrderStorageLocationModal';
import { weightStringToNumberInKgs, isExtraDeliveryOrder, isInHouseMerchant } from '../../utils/orderUtils';
import { newNotification } from '../../actions/uiActions';
import { bindActionCreators } from 'redux';
import { useSearchParams } from 'react-router-dom';
import { blanchCellId, blanchZoneId } from '../../constants';
import useInterval from '../../utils/useInterval';
import MannaWeigherAppIcon from '../../assets/svgs/MannaWeigherAppIcon.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ORDER_STATUS_UPDATED, API_ERROR, EXTRA_ORDER_CREATED_SUCCESS } from '../../actions/actionTypes';
import OrdersIdsDropDown from './components/OrdersIdsDropDown';
import { sortOrdersByPriority } from '../../utils/orderUtils';
import { getWeatherForCell } from '../../actions/weatherActions';
import { useOrderSSE2 } from '../../hooks/useOrderSSE2';
import { orderfulApi } from '../../services/api-instances';
import { updateOrderStatus } from '../../services/order-service';
import packageJson from '../../../package.json';

const newOrderPreparedSound = new Audio(require('../../assets/alert_simple.wav'));
newOrderPreparedSound.volume = 1;

interface WeigherAppProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
}

//Weigher app
const WeigherApp = ({
  orders,
  loading,
  getCells,
  getMerchantsInCell,
  setOrderWithinTimeFilter,
  newNotification,
}: WeigherAppProps) => {

  const dispatch = useDispatch();

  const cells = useSelector(selectCells);
  const merchants = useSelector(selectMerchants);
  
  const [landingZones, setLandingZones] = useState<LandingZone[]>([]);

  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [shouldShowOrderWeightEntryModalForOrderId, setShouldShowOrderWeightEntryModalForOrderId] = useState<string>('');
  const [orderIdWeightMap, setOrderIdWeightMap] = useState<{[key: string]: number}>({});

  const [isWithLoaderSectionExpanded, setIsWithLoaderSectionExpanded] = useState<boolean>(false);
  const [withLoaderOrders, setWithLoaderOrders] = useState<Order[]>([]);

  const [selectedOrderForModal, setSelectedOrderForModal] = useState<Order | null>(null);
  const [isOrderCardDetailsOpen, setIsOrderCardDetailsOpen] = useState<boolean>(false);
  
  const [searchParams, setSearchParams] = useSearchParams();

  const [showStorageLocationModal, setShowStorageLocationModal] = useState<boolean>(false);
  const [orderForStorageLocationModal, setOrderForStorageLocationModal] = useState<Order | null>(null);
  const [shouldShowWeatherWarning, setShouldShowWeatherWarning] = useState<boolean>(false);

  const fortyFiveSecondsInMs = 45000;

  const selectedCellId = parseInt(searchParams.get('cell')) || blanchCellId;
  const selectedLandingZone = searchParams.get('zone') || blanchZoneId;

  // Use WebSocket hook
  const { isConnected, error } = useOrderSSE2(selectedCellId, selectedLandingZone);

  useEffect(() => {
 
    if (!isConnected && error) {
      logToDatadog('weigher_app_SSE_disconnected');
    } else {
      logToDatadog('weigher_app_SSE_connected');
    }
  }, [isConnected, error]);

  // =========================================
  // Lifecycle / Effect Hooks
  // =========================================

  useEffect(() => {
    setOrderWithinTimeFilter(getMinutesSinceMidnight());
    getCells();
    getMerchantsInCell(selectedCellId);
  }, []);

  useEffect(() => {

    // Call these when landing zone changes
    if (cells) {
      getAllLandingZonesForCell(cells)
      .then(res => {
        setLandingZones(res);
      })
      .catch(err => console.error('Error getting landing zones:', err));
    }
  }, [selectedCellId, cells]);

  useInterval(() => {
    setOrderWithinTimeFilter(getMinutesSinceMidnight());
    getAndSetWeatherdata();
  }, fortyFiveSecondsInMs);

  useEffect(() => {
    const newfilteredOrders = filterOrders(orders);

    if (newfilteredOrders.length !== filteredOrders.length) {
      playNewOrderSound();
    }
    
    // Update selected order in order card order details modal if it's open
    if (selectedOrderForModal && isOrderCardDetailsOpen) {
      const updatedOrder = newfilteredOrders.find((order) => order.id === selectedOrderForModal.id);
      if (updatedOrder) {
        setCurrentlySelectedOrder(updatedOrder);
      }
    }
  
    setFilteredOrders(newfilteredOrders);
    
    // Filter and set loader orders
    const newLoaderOrders = filterLoaderOrders();
    setWithLoaderOrders(newLoaderOrders);
  }, [orders]);

  // =========================================
  // Weather Data Fetching
  // =========================================
  
  const getAndSetWeatherdata = async () => {
    if (!selectedCellId || !cells) {
      setShouldShowWeatherWarning(false);
      return;
    }

    const cell = cells?.find(c => c.id === selectedCellId);
    try {
      const minimumWindspeedForWarningToShow = 7; // in m/s
      const wx = await getWeatherForCell(cell);
      setShouldShowWeatherWarning(wx.windSpeed >= minimumWindspeedForWarningToShow);
    } catch (error) {
      logToDatadog('error_retrieving_weather_data', {cell, error}, 'error');
      setShouldShowWeatherWarning(false);
    }
  };

  //We want to get all orders that have occured since 00:00 this morning.
  //We use this function to get the minutes since midnight so that 
  //all orders since midnight for this landing zone are included in this component 
  const getMinutesSinceMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    
    const diffMs = now.getTime() - midnight.getTime();
    const minutesSinceMidnight = Math.floor(diffMs / (1000 * 60));
    
    return minutesSinceMidnight;
  };

  // =========================================
  // State & Data Management Functions
  // =========================================
  const setCurrentlySelectedOrder = (order: Order | null) => {
    const orderId = order?.id ?? null;
    setSelectedOrderForModal(order);
    dispatch(setSelectedOrderId((orderId)))
  }

  // =========================================
  // API Calls
  // =========================================
  const forceToReceived = async (order: Order): Promise<void> => {
    return updateOrderStatus(order.id, 'RECEIVED_FROM_STORE').then(() => {
       dispatch({
        type: ORDER_STATUS_UPDATED,
        payload: {
          orderId: order.id,
          status: 'RECEIVED_FROM_STORE',
        },
        meta: {
          success: {
            message: `Order ${order.id} status updated to received`
          }
        }
      });
    }).catch(error => {
      logToDatadog('forceToReceived_failed-support_app')

      dispatch({
        type: API_ERROR,
        payload: error,
        meta: { error: error }
      });
    })
  };

  const handleWeightChangeForOrder = async (orderId: string, newWeight: string): Promise<void> => {
    const weightAsNumber = weightStringToNumberInKgs(newWeight);
    const isUpdatingWeight = orderId in orderIdWeightMap;

    try {
      setOrderIdWeightMap({
        ...orderIdWeightMap,
        ...{[orderId]: weightAsNumber}
      });

      await API.post('delivery', `/payloadweight/${orderId}`, {
        body: {
          weight: weightAsNumber
        }
      });
      
      // Close weight modal
      setShouldShowOrderWeightEntryModalForOrderId(null);
      
      // Set the current order for storage location modal and show it
      const order = orders.find(order => order.id === orderId);
      
      if (order && !isUpdatingWeight) {
        showLocationStorageModalForOrder(order);
      }
    } catch (error) {
      setOrderIdWeightMap({
        ...orderIdWeightMap,
        ...{[orderId]: undefined}
      });

      logToDatadog('error_saving_weight_for_order', {error, orderId});
      newNotification({
        variant: 'error',
        message: 'Error saving weight for order'
      });
    }
  };

  const showLocationStorageModalForOrder = (order: Order) => {
    setOrderForStorageLocationModal(order);
    setShowStorageLocationModal(true);
  }

  const handleStorageLocationSelection = async (locations: BagLocation[]): Promise<void> => {
    if (!orderForStorageLocationModal) return;
    
    try {

      let bagLocations: OrderBagListRequest = {
        orderBags: []
      }

      for (var index = 0; index < locations.length; index++) {

        const bagNumber = index + 1;
        const storageLocation = locations[index];

        const bagLocation = {
          bagNumber: bagNumber,
          location: storageLocation
        }

        bagLocations.orderBags.push(bagLocation);
      }
      
      await orderfulApi.setLocationOfOrderBags(orderForStorageLocationModal.id, bagLocations);
      
      newNotification({
        variant: 'success',
        message: `Order storage location saved successfully`
      });
    } catch (error) {
      logToDatadog('error_saving_order_storage_location', {error, orderId: orderForStorageLocationModal.id}, 'error');
      newNotification({
        variant: 'error',
        message: 'Error saving order storage location'
      });
    } finally {
      setShowStorageLocationModal(false);
      setOrderForStorageLocationModal(null);
    }
  };

  const handleExtraOrderFromModal = async (orderId: string) => {
    try {
      const extraDeliveryOrder = await orderfulApi.createExtraDeliveryOrder(orderId)

      dispatch({
        type: EXTRA_ORDER_CREATED_SUCCESS,
        payload: {
          orderId: extraDeliveryOrder.id,
        },
        meta: {
          success: {
            message: `Extra delivery order created: ${extraDeliveryOrder.id}`
          }
        }
      });
    } catch (error) {

      dispatch({
        type: API_ERROR,
        payload: JSON.stringify(error),
        meta: { error: JSON.stringify(error) }
      });

      logToDatadog('error_creating_extra_delivery_order', {error}, 'error');
    } finally {
      setIsOrderCardDetailsOpen(false);
    }
  };

  const playNewOrderSound = async () => {
    try {
      await newOrderPreparedSound.play();
    } catch (error) {
      // noop - this throws an error if it's played before the user has interacted with the page.
    }
  };

  // =========================================
  // Filter and Predicate Functions
  // =========================================
  const filterOrders = (orderList: Order[]) => { 
    return orderList
      .filter(order => orderIsInLandingZone(order, selectedLandingZone))
      .sort(sortOrdersByPriority);
  };

  // Filter predicates for collector statuses
  const hasWaitingCollectorStatus = (order) => 
    order?.collectorStatus === CollectorStatus.WAITING_FOR_COLLECTOR_TO_ACCEPT || order?.collectorStatus === CollectorStatus.COLLECTOR_ASSIGNED;

  const hasEnRouteOrHandedOverCollectorStatus = (order) => 
    order?.collectorStatus === CollectorStatus.EN_ROUTE_TO_MERCHANT || 
    order?.collectorStatus === CollectorStatus.COLLECTOR_NEAR_MERCHANT ||
    order?.collectorStatus === CollectorStatus.EN_ROUTE_TO_MANNA_BASE || 
    order?.collectorStatus === CollectorStatus.COLLECTOR_NEAR_LZ ||
    order?.collectorStatus === CollectorStatus.AT_MANNA_BASE || 
    order?.collectorStatus === CollectorStatus.HANDED_OVER;

  // Filter predicates for order statuses
  const hasStatusWithMerchant = (order) => 
    ['STORE_ACCEPTED', 'STORE_PREPARING', 'PREPARING', 'STORE_PREPARED', 'PREPARED'].includes(order.status);

  const hasAWithYouOrderStatus = (order) => 
    ['RECEIVED_FROM_STORE', 'RECEIVED', 'READY'].includes(order.status);

  const isValidInHouseOrder = (order) => { 
    // Check if order is from an in-house merchant based on posId and check status
    return (['STORE_PREPARED', 'PREPARED'].includes(order.status) && isInHouseMerchant(order.posId, merchants));
  }

  // Combined predicates for each category
  const isWithMerchant = (order) => 
    hasStatusWithMerchant(order) && 
    order?.collectorStatus === CollectorStatus.WAITING_FOR_COLLECTOR_TO_ACCEPT;

  const isForCollection = (order) => 
    hasStatusWithMerchant(order) && 
    (hasWaitingCollectorStatus(order));

  const isWithCollector = (order) => 
    (hasStatusWithMerchant(order) && 
    (hasEnRouteOrHandedOverCollectorStatus(order) || isExtraDeliveryOrder(order) || isValidInHouseOrder(order))) ;

  const isWithYou = (order) => 
    hasAWithYouOrderStatus(order);

  const includedStatuses = ['DISPATCHED', 'DELIVERED'];
  const stillWithLoader = (order) => 
    includedStatuses.includes(order.status);

  const filterLoaderOrders = () => {
    return filteredOrders
      .filter(order => 
        !isWithMerchant(order) && 
        !isForCollection(order) && 
        !isWithCollector(order) && 
        !isWithYou(order) && 
        stillWithLoader(order)
      )
      .sort(sortOrdersByPriority);
  };

  // =========================================
  // Count Getter Functions
  // =========================================
  const getWithMerchantCount = () => {
    return filteredOrders
      .filter(isWithMerchant)
      .length;
  };

  const getForCollectionCount = () => {
    return filteredOrders
      .filter(isForCollection)
      .length;
  };

  const getWithCollectorCount = () => {
    return filteredOrders
      .filter(isWithCollector)
      .length;
  };

  const getWithYouCount = () => {
    return filteredOrders
      .filter(isWithYou)
      .length;
  };

  const getWithLoaderCount = () => {
    return withLoaderOrders.length;
  };

  // =========================================
  // UI Event Handlers
  // =========================================
  const handleOpenOrderCardDetailsModal = (order: Order) => {
    setCurrentlySelectedOrder(order);
    setIsOrderCardDetailsOpen(true);
  };

  const handleCloseOrderCardDetailsModal = () => {
    setIsOrderCardDetailsOpen(false);
    setCurrentlySelectedOrder(null);
  };

  const handleWeighOrderFromModal = () => {
    if (selectedOrderForModal) {
      setShouldShowOrderWeightEntryModalForOrderId(selectedOrderForModal.id);
    }
  };

  const handleLZUpdate = useCallback((event: React.ChangeEvent<any>) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const zoneValue = event.target.value;
    const landingZone = landingZones.find(lz => lz.id === zoneValue);
    newSearchParams.set('zone', landingZone?.id);
    newSearchParams.set('cell', landingZone?.cellId.toString());
    setSearchParams(newSearchParams);
  }, [landingZones, searchParams, setSearchParams]);

  // =========================================
  // UI/Rendering Functions
  // =========================================
  const getOrderCardForOrder = (order: Order): JSX.Element => { 
    return <WeigherAppOrderCard
      key={`ops-order-card-${order.id}`}
      order={order}
      orderIdWeightMap={orderIdWeightMap}
      isWithCollector={isWithCollector}
      shouldShowWeatherWarning={shouldShowWeatherWarning}
      onClick={() => handleOpenOrderCardDetailsModal(order)} 
    />;
  };

  const renderCountSection = () => (
    <Box sx={{ 
      p: 2,
      borderRight: '1px solid #EFEFEF',
    }}>
      <Box sx={{ mb: 4, userSelect: 'none' }}>
        <OrdersIdsDropDown 
          title="For Collection" 
          count={getForCollectionCount()} 
          orders={filteredOrders.filter(isForCollection)}
        />
      </Box>
      <Box sx={{ mb: 4, userSelect: 'none' }}>
        <OrdersIdsDropDown 
          title="With merchant" 
          count={getWithMerchantCount()} 
          orders={filteredOrders.filter(isWithMerchant)}
        />
      </Box>
    </Box>
  );

  const renderLoaderOrdersGrid = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {[...Array(9)].map((_, index) => (
            <Box key={index} sx={{ width: '200px', p: 1, flexShrink: 0 }}>
              <Card className="m-0.5 p-[20px_15px] mb-1.25">
                <Skeleton width="50%" />
                <Skeleton width="25%" />
              </Card>
            </Box>
          ))}
        </Box>
      );
    }

    const numRowsPerColumn = 3; 
    const columns = [];
    
    // Split orders into columns
    for (let i = 0; i < withLoaderOrders.length; i += numRowsPerColumn) {
      columns.push(withLoaderOrders.slice(i, i + numRowsPerColumn));
    }
    
    return (
      <Box sx={{ 
        display: 'flex',
        gap: '16px', 
        minHeight: '600px',
        width: 'fit-content', 
        minWidth: '100%' 
      }}>
        {columns.map((columnOrders, colIndex) => (
          <Box 
            key={`column-${colIndex}`}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px', 
              width: '400px',
              flexShrink: 0
            }}
          >
            {columnOrders.map(order => (
              <Box key={order.id}>
                {getOrderCardForOrder(order)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  // =========================================
  // Component Render
  // =========================================
  return (
    <>
      <OpsHeader
        selectedLandingZone={selectedLandingZone}
        landingZones={landingZones}
        logoSrc={MannaWeigherAppIcon}
        handleLZUpdate={handleLZUpdate}
        isConnected={isConnected}
      />
      
      {/* Main container */}
      <Box sx={{ 
        position: 'relative',
        height: 'calc(100vh - 64px)', 
        overflow: 'hidden',
      }}>
        {/* Main content with three columns */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          height: isWithLoaderSectionExpanded ? 'calc(100vh - 64px - 600px - 56px)' : 'calc(100vh - 64px - 56px)',
          overflow: 'hidden',
        }}>
          {/* Column 1: For Collection / With Merchant */}
          <Box sx={{ 
            p: 2,
            borderRight: '1px solid #EFEFEF',
          }}>
            {renderCountSection()}
          </Box>

          {/* Column 2: With collector */}
          <Box sx={{ 
            width: '44%', 
            p: 2,
            borderRight: '1px solid #EFEFEF',
            overflowY: 'auto'
          }}>
            <Box display="flex" p={1} justifyContent="space-between" alignItems="center">
              <h3 className="text-2xl font-semibold leading-6 text-gray-900">Collector</h3>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#666' }}>
                {getWithCollectorCount()}
              </Typography>
            </Box>
            
            {loading && (
              <>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
              </>
            )}
            
            {!loading &&
              filteredOrders
              .filter(isWithCollector)
                .map(order => getOrderCardForOrder(order))}
          </Box>

          {/* Column 3: With you */}
          <Box sx={{ 
            width: '44%', 
            p: 2,
            overflowY: 'auto',
          }}>
            <Box display="flex" p={1} justifyContent="space-between" alignItems="center">
              <h3 className="text-2xl font-semibold leading-6 text-gray-900">With Manna</h3>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#666' }}>
                {getWithYouCount()}
              </Typography>
            </Box>
            
            {loading && (
              <>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
                <Card className="m-0.5 p-[20px_15px] mb-1.25">
                  <Skeleton width="50%" />
                  <Skeleton width="25%" />
                </Card>
              </>
            )}
            
            {!loading &&
              filteredOrders
                .filter(isWithYou)
                .sort(sortOrdersByPriority)
                .map(order => getOrderCardForOrder(order))}
          </Box>
        </Box>

        {/* With Loader Section - fixed positioning to always stay at the bottom */}
        <Box sx={{ 
          position: 'fixed', 
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100, 
          borderTop: '1px solid #EFEFEF',
          backgroundColor: 'white',
          boxShadow: isWithLoaderSectionExpanded ? '0px -2px 10px rgba(0,0,0,0.1)' : 'none',
          maxHeight: isWithLoaderSectionExpanded ? '656px' : '56px', 
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box 
            onClick={() => setIsWithLoaderSectionExpanded(!isWithLoaderSectionExpanded)}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 2,
              height: '56px', 
              cursor: 'pointer',
              borderBottom: isWithLoaderSectionExpanded ? '1px solid #EFEFEF' : 'none',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                With loader
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#999' }}>
                v{packageJson.version}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#666', mr: 2 }}>
                {getWithLoaderCount()}
              </Typography>
              <IconButton size="small">
                {isWithLoaderSectionExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={isWithLoaderSectionExpanded}>
            <Box sx={{ 
              p: 2, 
              maxHeight: '600px', 
              overflowX: 'auto',
              overflowY: 'auto', 
            }}>
              {renderLoaderOrdersGrid()}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* OrderCardDetails Modal */}
      {selectedOrderForModal ? (
        <WeigherAppOrderCardDetails
          open={isOrderCardDetailsOpen}
          order={selectedOrderForModal}
          orderIdWeightMap={orderIdWeightMap}
          merchantLogoUrl={(selectedOrderForModal as any)?.merchantImageUrl}
          onClose={handleCloseOrderCardDetailsModal}
          onWeigh={handleWeighOrderFromModal}
          onExtraOrder={handleExtraOrderFromModal}
          pushToReceived={forceToReceived}
          showLocationStorageModalForOrder={showLocationStorageModalForOrder}
        />
      ): null}
      
      {/* Weight Dialog */}
      {shouldShowOrderWeightEntryModalForOrderId ? <OrderWeightDialog
        order={orders?.find(({id}) => id === shouldShowOrderWeightEntryModalForOrderId)}
        initialValue={`${orderIdWeightMap[shouldShowOrderWeightEntryModalForOrderId] || ''}`}
        onSubmit={(val: string) => {
          handleWeightChangeForOrder(shouldShowOrderWeightEntryModalForOrderId, val);
        }}
        onCloseButtonPressed={() => {
          setShouldShowOrderWeightEntryModalForOrderId(null);
        }} /> : null}

      {/* Storage Location Modal */}
      {orderForStorageLocationModal && (
        <OrderStorageLocationModal
          open={showStorageLocationModal}
          order={orderForStorageLocationModal}
          onDone={handleStorageLocationSelection}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  orders: state.orders.ordersToFilter,
  loading: state.orders.loading,
});

const mapDispatchToProps = (dispatch: AppDispatch) =>
  bindActionCreators(
    {
      getCells,
      getMerchantsInCell,
      setOrderWithinTimeFilter,
      newNotification,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(WeigherApp);